'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { INITIAL_CARDS, KANBAN_COLUMNS, KanbanCard } from './kanbanMockData';
import KanbanHeader from './KanbanHeader';
import CardDetailSheet from './CardDetailSheet';
import { toast } from 'sonner';

const KanbanColumnsView = dynamic(() => import('./KanbanColumnsView'), { ssr: false });

export default function KanbanBoardContent() {
  const [cards, setCards] = useState<KanbanCard[]>(INITIAL_CARDS);
  const [activeColumnIndex, setActiveColumnIndex] = useState(1); // mobile swipe state
  const [selectedCard, setSelectedCard] = useState<KanbanCard | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleCardMove = (cardId: string, targetColumnId: string) => {
    // BACKEND INTEGRATION: PATCH /api/cards/:cardId with { columnId, status }
    setCards((prev) =>
      prev.map((c) => {
        if (c.id !== cardId) return c;
        const col = KANBAN_COLUMNS.find((col) => col.id === targetColumnId);
        return { ...c, columnId: targetColumnId, status: col?.title ?? c.status };
      })
    );
  };

  const handleCardUpdate = (updated: KanbanCard) => {
    // BACKEND INTEGRATION: PUT /api/cards/:cardId with updated card data
    setCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelectedCard(updated);
    toast.success('Card updated');
  };

  const handleCardCreate = (columnId: string, title: string) => {
    // BACKEND INTEGRATION: POST /api/cards with { columnId, title, projectId }
    const col = KANBAN_COLUMNS.find((c) => c.id === columnId);
    const newCard: KanbanCard = {
      id: `card-${Date.now()}`,
      title,
      description: '',
      status: col?.title ?? 'Backlog',
      priority: 'Medium',
      assignee: null,
      storyPoints: 1,
      dueDate: '',
      labels: [],
      columnId,
    };
    setCards((prev) => [...prev, newCard]);
    toast.success('Card added');
  };

  const handleCardDelete = (cardId: string) => {
    // BACKEND INTEGRATION: DELETE /api/cards/:cardId
    setCards((prev) => prev.filter((c) => c.id !== cardId));
    setSelectedCard(null);
    toast.success('Card deleted');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // BACKEND INTEGRATION: GET /api/projects/:projectId/cards
    await new Promise((r) => setTimeout(r, 1000));
    setRefreshing(false);
    toast.success('Board refreshed');
  };

  const columnsWithCards = KANBAN_COLUMNS.map((col) => ({
    ...col,
    cards: cards.filter((c) => c.columnId === col.id),
  }));

  return (
    <div className="flex flex-col h-full">
      <KanbanHeader
        activeColumnIndex={activeColumnIndex}
        onColumnChange={setActiveColumnIndex}
        columns={KANBAN_COLUMNS}
        totalCards={cards.length}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <div className="flex-1 overflow-hidden">
        <KanbanColumnsView
          columnsWithCards={columnsWithCards}
          activeColumnIndex={activeColumnIndex}
          onColumnChange={setActiveColumnIndex}
          onCardClick={setSelectedCard}
          onCardMove={handleCardMove}
          onCardCreate={handleCardCreate}
        />
      </div>

      {selectedCard && (
        <CardDetailSheet
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          onUpdate={handleCardUpdate}
          onDelete={handleCardDelete}
        />
      )}
    </div>
  );
}